// Premium Client GST Tax Invoice — Enterprise Edition (matches Owner Receipt tier)
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BRAND, rgb, fmtINR, fmtDateTime, computeDocHash, amountInWordsINR,
  roundRectFill, roundRectFS, drawDiamond, drawStatusPill,
} from './shared';
import {
  computeBookingTaxQuote,
  splitStoredGst,
  DEFAULT_SAC,
  PLATFORM_SUPPLIER,
  STATE_CODES,
  round2,
  validateGstin,
} from '../gst/gstRules';
import { formatHoursHm } from '../durationHours';

const COMPANY = {
  name: 'DEVELOPMENT EXPRESS',
  tagline: 'Development Express · Enterprise Edition',
  md: 'Om Chavan | B.Tech (Civil) | Managing Director',
  addr: 'Karad, Satara, Maharashtra - 415110',
  phone: '+91-8408000084',
  email: 'om.chavan2026@zohomail.in',
  web: 'developmentexpress.in',
};

const DUMMY_GSTIN = new Set(['27ABCDE1234F1Z5', '27ABCDE1234F1Z0']);
const DUMMY_REG = new Set(['MH-09-AB-1234']);
const GSTIN_MISSING = '⚠ GSTIN not provided';

const PIN_PREFIX_STATE = {
  11: '07', 12: '06', 13: '06', 14: '03', 15: '03', 16: '03',
  17: '02', 18: '01', 19: '01',
  20: '09', 21: '09', 22: '09', 23: '09', 24: '09', 25: '09', 26: '09', 27: '09', 28: '09',
  30: '08', 31: '08', 32: '08', 33: '08', 34: '08',
  36: '24', 37: '24', 38: '24', 39: '24',
  40: '27', 41: '27', 42: '27', 43: '27', 44: '27',
  45: '23', 46: '23', 47: '23', 48: '23', 49: '22',
  50: '36', 51: '37', 52: '37', 53: '37',
  56: '29', 57: '29', 58: '29', 59: '29',
  60: '33', 61: '33', 62: '33', 63: '33', 64: '33',
  67: '32', 68: '32', 69: '32',
  70: '19', 71: '19', 72: '19', 73: '19', 74: '19',
  75: '21', 76: '21', 77: '21', 78: '18',
  80: '10', 81: '10', 82: '20', 83: '20', 84: '10', 85: '10',
};

function displayGstin(value) {
  const v = String(value || '').trim().toUpperCase();
  if (!v || v === '—' || v === 'URP' || DUMMY_GSTIN.has(v)) return GSTIN_MISSING;
  const check = validateGstin(v, { requireChecksum: false });
  return check.ok ? v : GSTIN_MISSING;
}

function resolveRegNo(booking) {
  const machine = Array.isArray(booking.machines) ? booking.machines[0] : (booking.machines || booking.machine);
  const raw = String(
    booking.regNo
    || booking.machine_reg_no
    || booking.machineRegNo
    || machine?.reg_no
    || machine?.regNo
    || ''
  ).trim();
  if (!raw || DUMMY_REG.has(raw)) return '—';
  return raw;
}

function stateCodeFromSite(booking) {
  if (booking.siteStateCode) return String(booking.siteStateCode).padStart(2, '0');
  const addr = String(booking.siteAddress || booking.site_address || booking.location || '').toLowerCase();
  for (const [code, name] of Object.entries(STATE_CODES)) {
    if (name && addr.includes(String(name).toLowerCase())) return code;
  }
  const pin = String(booking.sitePincode || booking.site_pincode || '').replace(/\D/g, '');
  if (pin.length === 6) {
    const mapped = PIN_PREFIX_STATE[Number(pin.slice(0, 2))];
    if (mapped) return mapped;
  }
  return null;
}

function buildInvoiceSerial(booking) {
  const given = String(booking.invoiceNo || '').trim();
  if (/^INV\/\d{6}\/\d{4}$/.test(given)) return given;
  if (/^DE\/INV\/\d{6}\/\d{4}$/.test(given)) return given.replace(/^DE\//, '');
  const now = new Date();
  const yyyymm = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  const ref = String(
    booking.bookingRef || booking.booking_ref || booking.bookingId || booking.id || ''
  );
  const seq = (ref.replace(/\D/g, '').slice(-4) || '0001').padStart(4, '0').slice(-4);
  return `INV/${yyyymm}/${seq}`;
}

const PDF_FOOTER_TAG = 'DE Premium PDF · v2026-05-30';

const MARGIN = { top: 25, bottom: 20, left: 15, right: 15 };
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN.left - MARGIN.right; // 180mm

const amountInWordsFallback = amountInWordsINR;

const drawCoverPage = (doc, data) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  doc.setFillColor(...rgb(BRAND.COVER_BG));
  doc.rect(0, 0, w, h, 'F');

  doc.setDrawColor(31, 27, 18);
  doc.setLineWidth(0.1);
  for (let i = 0; i < h; i += 2.5) {
    doc.line(0, i, w, i + 1);
  }

  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.5);
  [[15, 15, 15, 32, 32, 15], [w - 15, 15, w - 15, 32, w - 32, 15],
    [15, h - 15, 15, h - 32, 32, h - 15], [w - 15, h - 15, w - 15, h - 32, w - 32, h - 15]]
    .forEach(([x1, y1, x2, y2, x3, y3]) => {
      doc.line(x1, y1, x2, y2);
      doc.line(x1, y1, x3, y3);
    });

  const emblemY = 52;
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.6);
  doc.circle(w / 2, emblemY, 18, 'S');
  doc.setLineWidth(0.25);
  doc.circle(w / 2, emblemY, 14, 'S');
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('DE', w / 2, emblemY + 2, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.text('SINCE 2011', w / 2, emblemY + 8, { align: 'center' });

  const bandY = h - 188;
  doc.setFillColor(...rgb(BRAND.COVER_GOLD));
  doc.rect(0, bandY, w, 0.3, 'F');
  doc.rect(0, bandY + 28, w, 0.3, 'F');

  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(COMPANY.name, w / 2, bandY + 10, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('M A C H I N E   O S   ·   C L I E N T   T A X   I N V O I C E', w / 2, bandY + 17, { align: 'center' });

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('O F F I C I A L   G S T   T A X   I N V O I C E', w / 2, bandY + 38, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('GST Tax Invoice', w / 2, bandY + 52, { align: 'center' });
  doc.text('(Original for Recipient)', w / 2, bandY + 62, { align: 'center' });

  const dy = bandY + 74;
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.4);
  doc.line(w / 2 - 30, dy, w / 2 - 4, dy);
  doc.line(w / 2 + 4, dy, w / 2 + 30, dy);
  drawDiamond(doc, w / 2, dy, 1.5, BRAND.COVER_GOLD);

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Total Invoice Value (incl. GST)', w / 2, dy + 12, { align: 'center' });
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text(fmtINR(data.total), w / 2, dy + 24, { align: 'center' });

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Balance Due: ${fmtINR(data.balance)}  ·  Work: ${data.workPeriod}`, w / 2, dy + 34, { align: 'center' });

  const infoY = h - 42;
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('INVOICE NO.', 20, infoY);
  doc.text('BILL TO', w / 2, infoY, { align: 'center' });
  doc.text('BOOKING REF', w - 20, infoY, { align: 'right' });

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(data.invoiceNo, 20, infoY + 5);
  doc.text(data.clientName, w / 2, infoY + 5, { align: 'center' });
  doc.text(data.bookingId, w - 20, infoY + 5, { align: 'right' });

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.text(`${COMPANY.addr}  ·  ${COMPANY.phone}`, w / 2, h - 18, { align: 'center' });
  doc.text(`Supplier GSTIN: ${data.supplierGstin}`, w / 2, h - 13, { align: 'center' });
};

const drawInsideHeaderFooter = (doc, page, totalPages, invoiceNo, docHash, supplierGstin) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const left = MARGIN.left;
  const right = w - MARGIN.right;

  doc.setFillColor(...rgb(BRAND.GOLD));
  doc.rect(0, 0, w, 4, 'F');
  doc.setFillColor(...rgb(BRAND.GOLD_DEEP));
  doc.rect(0, 4, w, 0.5, 'F');

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('DEVELOPMENT EXPRESS', left, 11);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('Development Express  ·  Client GST Invoice', left, 15);

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('GST Tax Invoice - Detail', w / 2, 13, { align: 'center' });

  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text('OFFICIAL  ·  TAX INVOICE', right, 11, { align: 'right' });
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.text(`${invoiceNo}  ·  GSTIN ${supplierGstin || GSTIN_MISSING}`, right, 15, { align: 'right' });

  doc.setDrawColor(...rgb(BRAND.BORDER));
  doc.setLineWidth(0.2);
  doc.line(left, 18, right, 18);

  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.4);
  doc.line(left, h - 16, w / 2 - 4, h - 16);
  doc.line(w / 2 + 4, h - 16, right, h - 16);
  drawDiamond(doc, w / 2, h - 16, 1.3, BRAND.GOLD);

  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text(`${COMPANY.web}  ·  ${COMPANY.email}`, left, h - 11);
  doc.text('(c) 2026 Development Express. All rights reserved.', w / 2, h - 11, { align: 'center' });
  doc.text(`Page ${page} of ${totalPages}`, right, h - 11, { align: 'right' });
  doc.setTextColor(...rgb(BRAND.MUTED_LT));
  doc.setFontSize(5.5);
  doc.text(`${PDF_FOOTER_TAG} · ${docHash.substring(0, 12)}`, left, h - 6);
  doc.text(`Generated ${fmtDateTime(new Date().toISOString())}`, right, h - 6, { align: 'right' });
};

const drawKPICard = (doc, x, y, cw, ch, label, value, sub, accent) => {
  roundRectFill(doc, x + 0.4, y + 0.4, cw, ch, 1.5, '#EFEFEF');
  roundRectFS(doc, x, y, cw, ch, 1.5, '#FFFFFF', BRAND.BORDER, 0.2);
  doc.setFillColor(...rgb(accent));
  doc.rect(x, y, cw, 1, 'F');
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text(label.toUpperCase(), x + 3, y + 5);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(value, x + 3, y + 12);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  const subLines = doc.splitTextToSize(String(sub || ''), cw - 6);
  doc.text(subLines[0], x + 3, y + 17);
};

const drawInfoCard = (doc, x, y, cw, ch, title, rows) => {
  roundRectFS(doc, x, y, cw, ch, 1.5, '#FFFFFF', BRAND.BORDER, 0.25);
  doc.setFillColor(...rgb(BRAND.GOLD_PALE));
  doc.rect(x, y, cw, 7, 'F');
  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.15);
  doc.line(x, y + 7, x + cw, y + 7);
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(title.toUpperCase(), x + 3, y + 4.8);
  rows.forEach(([k, v], i) => {
    const ry = y + 12 + i * 6;
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(k, x + 3, ry);
    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    const lines = doc.splitTextToSize(String(v || '—'), cw - 32);
    doc.text(lines[0], x + 28, ry);
  });
};

export async function generatePremiumClientInvoice(booking = {}, options = {}) {
  const { download = true } = options;
  const hours = Number(booking.hours || 75);
  const base = round2(Number(booking.baseAmount || 105000));
  const clientGstRaw = String(booking.clientGST || '').trim();
  const clientGstValid = displayGstin(clientGstRaw);
  const recipientGstin = clientGstValid !== GSTIN_MISSING ? clientGstRaw.toUpperCase() : null;
  const siteStateCode = stateCodeFromSite(booking);
  const placeOpts = {
    recipientGstin,
    recipientStateCode: booking.recipientStateCode || null,
    siteStateCode,
  };
  // Stored split is authoritative — server computed it at booking time (Rule 46).
  // Only trusted when it actually carries tax; a zero split against a non-zero
  // GST total would print CGST/SGST 0.00 next to a GST 18% figure.
  const storedCgst = Number(booking.cgstAmount ?? 0);
  const storedSgst = Number(booking.sgstAmount ?? 0);
  const storedIgst = Number(booking.igstAmount ?? 0);
  const storedSplitTotal = round2(storedCgst + storedSgst + storedIgst);
  const hasStoredSplit = (booking.cgstAmount != null || booking.igstAmount != null)
    && storedSplitTotal > 0;
  let taxQuote;
  if (hasStoredSplit) {
    const isInterState = storedIgst > 0;
    const storedPosCode = booking.placeOfSupply
      ? String(booking.placeOfSupply).padStart(2, '0')
      : null;
    taxQuote = {
      taxable: base,
      rate: base > 0 ? round2(storedSplitTotal / base) : 0,
      gst: storedSplitTotal,
      cgst: storedCgst,
      sgst: storedSgst,
      igst: storedIgst,
      total: round2(base + storedSplitTotal),
      interState: isInterState,
      supplyType: booking.supplyType
        || (isInterState ? 'INTER_STATE' : 'INTRA_STATE'),
      placeOfSupply: storedPosCode
        ? { code: storedPosCode, name: STATE_CODES[storedPosCode] || `State ${storedPosCode}` }
        : null,
    };
  } else {
    // Historical rows written before the split was persisted — re-derive as before.
    taxQuote = booking.gstAmount != null && Number(booking.gstAmount) >= 0
      ? splitStoredGst(base, Number(booking.gstAmount), placeOpts)
      : computeBookingTaxQuote(base, placeOpts);
  }
  const gst = round2(taxQuote.gst);
  const cgst = round2(taxQuote.cgst);
  const sgst = round2(taxQuote.sgst);
  const igst = round2(taxQuote.igst || 0);
  const interState = Boolean(taxQuote.interState);
  const total = round2(taxQuote.grandTotal != null ? taxQuote.grandTotal : taxQuote.total);
  const exclusiveRate = hours > 0 ? round2(base / hours) : 0;
  const hoursLabel = formatHoursHm(hours);
  const exclusiveRateLabel = `${hoursLabel} × Rs ${exclusiveRate.toFixed(2)}/hr (excl. GST)`;
  const placeOfSupply = taxQuote.placeOfSupply
    || { name: 'Maharashtra', code: '27' };
  const placeOfSupplyLabel = `${placeOfSupply.name} (${placeOfSupply.code})`;
  const supplierGstin = displayGstin(PLATFORM_SUPPLIER.gstin);
  const gstPathLabel = interState ? 'IGST 18%' : 'CGST 9% + SGST 9%';
  const advance = Number(booking.advancePaid || 0);
  const extAdv = Number(
    booking.extensionAdvances || 0
  );
  const balance = Math.max(
    0, round2(total - advance - extAdv)
  );
  const isContract = String(booking.bookingType || '').toLowerCase() === 'contract';
  let bookingAdvancePaid = null;
  let mobilizationChargePaid = null;
  if (isContract && advance > 0) {
    const mobBy = String(booking.mobilizationTransportBy || '').toLowerCase();
    const mobTaxable = Number(booking.mobilizationTransportAmount || 0);
    if (mobBy === 'client' && mobTaxable > 0) {
      const mobQuote = computeBookingTaxQuote(mobTaxable, placeOpts);
      mobilizationChargePaid = Math.min(advance, Number(mobQuote.grandTotal || 0));
      bookingAdvancePaid = Math.max(0, Math.round((advance - mobilizationChargePaid) * 100) / 100);
    }
  }
  const canSplitContractPaid = isContract
    && bookingAdvancePaid != null
    && mobilizationChargePaid != null
    && mobilizationChargePaid > 0;

  const invoiceNo = buildInvoiceSerial(booking);
  const bookingId = booking.bookingId || booking.bookingRef || booking.booking_ref || 'BK-2026-041001';
  const invoiceDate = new Date().toLocaleDateString('en-IN');
  const dueDate = booking.dueDate || 'Pay via Wallet';
  const clientName = booking.clientName || 'Patil Builders Pvt. Ltd.';
  const docHash = await computeDocHash(invoiceNo, invoiceDate, total);

  const data = {
    invoiceNo,
    bookingId,
    invoiceDate,
    dueDate,
    clientName,
    supplierGstin,
    base,
    gst,
    cgst,
    sgst,
    igst,
    interState,
    total,
    advance,
    balance,
    hours,
    exclusiveRate,
    exclusiveRateLabel,
    gstPathLabel,
    placeOfSupplyLabel,
    words: amountInWordsFallback(total),
    clientAddr: booking.clientAddr || 'Karad, Satara - 415110',
    clientGST: clientGstValid,
    clientPhone: booking.clientPhone || '—',
    clientEmail: booking.clientEmail || '—',
    machineName: booking.machineName || 'JCB 3DX Backhoe Loader',
    regNo: resolveRegNo(booking),
    operator: booking.operator || 'Assigned Operator',
    location: booking.location || booking.siteAddress || booking.site_address || 'Karad, Satara',
    workPeriod: booking.workPeriod || '01-10 Apr 2026',
  };

  const statusLabel = balance <= 0 ? 'Paid' : 'Due';
  const statusBg = balance <= 0 ? BRAND.EMERALD_LT : BRAND.AMBER_LT;
  const statusFg = balance <= 0 ? BRAND.EMERALD : BRAND.AMBER;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth() || PAGE_W;
  const contentW = (w - MARGIN.left - MARGIN.right) || CONTENT_W;

  drawCoverPage(doc, data);

  doc.addPage();
  let y = MARGIN.top;

  const ensureSpace = (neededMm) => {
    const pageH = doc.internal.pageSize.getHeight() || PAGE_H;
    if (y + neededMm > pageH - MARGIN.bottom) {
      doc.addPage();
      y = MARGIN.top;
    }
  };

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Invoice Breakdown', MARGIN.left, y);
  drawStatusPill(doc, w - MARGIN.right - 22, y - 4, 22, 6, statusLabel, statusBg, statusFg);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(`Invoice Date: ${invoiceDate}  ·  Due: ${dueDate}`, MARGIN.left, y + 5);
  y += 12;

  const cardW = (contentW - 8) / 3;
  drawKPICard(doc, MARGIN.left, y, cardW, 20, 'Taxable Amount', fmtINR(base), exclusiveRateLabel, BRAND.GOLD);
  drawKPICard(doc, MARGIN.left + cardW + 4, y, cardW, 20, 'GST @ 18%', fmtINR(gst), gstPathLabel, BRAND.SAPPHIRE);
  const paidAtBookingSub = !advance
    ? 'Pay via Platform Wallet'
    : canSplitContractPaid
      ? `Booking Advance ${fmtINR(bookingAdvancePaid)} + Mobilization Charge ${fmtINR(mobilizationChargePaid)}`
      : isContract
        ? `Paid at booking (Booking Advance + Mobilization Charge) ${fmtINR(advance)}`
        : `Advance paid ${fmtINR(advance)}`;
  drawKPICard(doc, MARGIN.left + (cardW + 4) * 2, y, cardW, 20, 'Balance Due', fmtINR(balance), paidAtBookingSub, BRAND.EMERALD);
  y += 26;

  const halfW = (contentW - 4) / 2;
  drawInfoCard(doc, MARGIN.left, y, halfW, 44, 'Bill To - Client', [
    ['Name', data.clientName],
    ['Address', data.clientAddr],
    ['GSTIN', data.clientGST],
    ['Phone', data.clientPhone],
    ['Email', data.clientEmail],
  ]);
  drawInfoCard(doc, MARGIN.left + halfW + 4, y, halfW, 44, 'Work Site Details', [
    ['Machine', data.machineName],
    ['Reg. No.', data.regNo],
    ['Operator', data.operator],
    ['Location', data.location],
    ['Period', data.workPeriod],
  ]);
  y += 50;

  roundRectFS(doc, MARGIN.left, y, contentW, 14, 1.5, '#FFFFFF', BRAND.BORDER, 0.25);
  doc.setFillColor(...rgb(BRAND.GOLD_PALE));
  doc.rect(MARGIN.left, y, contentW, 5, 'F');
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text('GST PARTICULARS (RULE 46)', MARGIN.left + 3, y + 3.6);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(`Place of Supply: ${placeOfSupplyLabel}`, MARGIN.left + 3, y + 10.5);
  doc.text('Reverse Charge: No', MARGIN.left + 73, y + 10.5);
  doc.text(`Supplier GSTIN: ${supplierGstin}`, MARGIN.left + 115, y + 10.5);
  y += 18;

  const usageHours = Number(booking.attachmentUsageHours);
  const attHourly = Number(booking.attachmentHourlyRate || 0);
  const hasAttachmentWork = Number.isFinite(usageHours)
    && usageHours > 0
    && attHourly > 0
    && !isContract;
  const attachmentWorkBase = hasAttachmentWork
    ? Math.round(usageHours * attHourly * 100) / 100
    : 0;
  const machineWorkBase = hasAttachmentWork
    ? Math.max(0, Math.round((base - attachmentWorkBase) * 100) / 100)
    : base;
  const attLabel = booking.attachmentLabel || booking.selectedAttachment || 'Attachment';

  const sac = DEFAULT_SAC;
  const machineExclusiveRate = hours > 0 ? round2(machineWorkBase / hours) : exclusiveRate;
  const rateCell = (rate) => `Rs ${round2(rate).toFixed(2)}`;

  const lineBody = hasAttachmentWork
    ? [
      [
        {
          content: `Base Machine Charge\nInclusive of Operator + Fuel\n${hoursLabel} × Rs ${machineExclusiveRate.toFixed(2)}/hr (excl. GST)`,
          styles: { fontStyle: 'bold', cellPadding: { top: 3, bottom: 3, left: 3, right: 2 } },
        },
        sac,
        hoursLabel,
        rateCell(machineExclusiveRate),
        fmtINR(machineWorkBase),
        '',
        '',
      ],
      [
        {
          content: `${attLabel} Work\n${formatHoursHm(usageHours)} @ Rs ${round2(attHourly).toFixed(2)}/hr (excl. GST)`,
          styles: { fontStyle: 'bold', cellPadding: { top: 3, bottom: 3, left: 3, right: 2 } },
        },
        sac,
        formatHoursHm(usageHours),
        rateCell(attHourly),
        fmtINR(attachmentWorkBase),
        '',
        '',
      ],
      [
        {
          content: 'Combined taxable (before GST)',
          styles: { fontStyle: 'bold', cellPadding: { top: 3, bottom: 3, left: 3, right: 2 } },
        },
        '',
        '',
        '',
        fmtINR(base),
        fmtINR(gst),
        fmtINR(total),
      ],
    ]
    : [[
      {
        content: `${data.machineName}\nInclusive of Operator + Fuel\n${exclusiveRateLabel}`,
        styles: { fontStyle: 'bold', cellPadding: { top: 3, bottom: 3, left: 3, right: 2 } },
      },
      sac,
      hoursLabel,
      rateCell(exclusiveRate),
      fmtINR(base),
      fmtINR(gst),
      fmtINR(total),
    ]];

  autoTable(doc, {
    startY: y,
    head: [['Description', 'HSN', 'Qty/Hrs', 'Rate', 'Taxable', 'GST 18%', 'Total']],
    body: lineBody,
    foot: (() => {
      const paidFootRows = canSplitContractPaid
        ? [
          ['Booking Advance (Wallet)', '', '', '', '', `(${fmtINR(bookingAdvancePaid)})`, ''],
          ['Mobilization Charge (Wallet)', '', '', '', '', `(${fmtINR(mobilizationChargePaid)})`, ''],
        ]
        : isContract
          ? [['Paid at booking (Booking Advance + Mobilization Charge)', '', '', '', '', `(${fmtINR(advance)})`, '']]
          : [['Advance Paid (Wallet)', '', '', '', '', `(${fmtINR(advance)})`, '']];
      const taxFootRows = interState
        ? [['IGST @ 18%', '', '', '', '', fmtINR(igst), '']]
        : [
          ['CGST @ 9%', '', '', '', '', fmtINR(cgst), ''],
          ['SGST @ 9%', '', '', '', '', fmtINR(sgst), ''],
        ];
      return [
        ['Taxable Amount', '', '', '', fmtINR(base), '', ''],
        ...taxFootRows,
        ...paidFootRows,
        ['BALANCE DUE', '', '', '', '', '', fmtINR(balance)],
      ];
    })(),
    styles: {
      font: 'helvetica', fontSize: 7.5, cellPadding: 3.5, valign: 'middle',
      overflow: 'linebreak',
      lineColor: rgb(BRAND.BORDER), lineWidth: 0.1,
    },
    headStyles: {
      fillColor: rgb(BRAND.GOLD_PALE), textColor: rgb(BRAND.GOLD_DARK),
      fontStyle: 'bold', fontSize: 7,
    },
    footStyles: {
      fillColor: rgb(BRAND.GOLD_PALE), textColor: rgb(BRAND.INK),
      fontStyle: 'bold', fontSize: 7.5,
    },
    alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
    columnStyles: {
      0: { cellWidth: 48, fontStyle: 'bold', textColor: rgb(BRAND.INK) },
      1: { cellWidth: 14, halign: 'center' },
      2: { cellWidth: 22, halign: 'center' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 26, halign: 'right' },
      5: { cellWidth: 24, halign: 'right', textColor: rgb(BRAND.SAPPHIRE) },
      6: { cellWidth: 24, halign: 'right', fontStyle: 'bold', textColor: rgb(BRAND.EMERALD) },
    },
    margin: {
      top: MARGIN.top,
      bottom: MARGIN.bottom,
      left: MARGIN.left,
      right: MARGIN.right,
    },
    didParseCell: (hook) => {
      const footRow = hook.row.index;
      const taxRowCount = interState ? 1 : 2;
      const paidStart = 1 + taxRowCount;
      const paidCount = canSplitContractPaid ? 2 : 1;
      const balanceRow = paidStart + paidCount;
      if (hook.section === 'foot' && footRow === balanceRow && hook.column.index === 6) {
        hook.cell.styles.fillColor = rgb(BRAND.EMERALD);
        hook.cell.styles.textColor = [255, 255, 255];
        hook.cell.styles.fontSize = 9;
      }
      if (
        hook.section === 'foot'
        && footRow >= paidStart
        && footRow < balanceRow
        && hook.column.index === 5
      ) {
        hook.cell.styles.textColor = rgb(BRAND.EMERALD);
      }
    },
    didDrawPage: () => {
      const page = doc.internal.getCurrentPageInfo?.().pageNumber
        ?? doc.internal.getNumberOfPages();
      if (page < 2) return;
      drawInsideHeaderFooter(
        doc,
        page,
        doc.internal.getNumberOfPages(),
        invoiceNo,
        docHash,
        supplierGstin,
      );
    },
  });

  y = doc.lastAutoTable.finalY + 6;

  ensureSpace(35);
  roundRectFS(doc, MARGIN.left, y, contentW, 16, 1.5, BRAND.GOLD_PALE, BRAND.GOLD, 0.3);
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('AMOUNT IN WORDS', MARGIN.left + 3, y + 5);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const wordLines = doc.splitTextToSize(String(data.words || ''), contentW - 6);
  doc.text(wordLines[0], MARGIN.left + 3, y + 11);

  y += 22;

  ensureSpace(35);
  roundRectFS(doc, MARGIN.left, y, contentW, 20, 1.5, '#FFFFFF', BRAND.BORDER, 0.25);
  doc.setFillColor(...rgb(BRAND.GOLD_PALE));
  doc.rect(MARGIN.left, y, contentW, 6, 'F');
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('PAYMENT DETAILS - DEVELOPMENT EXPRESS', MARGIN.left + 3, y + 4.2);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('Wallet-Only Platform', MARGIN.left + 3, y + 11);
  doc.text(`Supplier GSTIN: ${supplierGstin}`, MARGIN.left + 73, y + 11);
  doc.setTextColor(...rgb(BRAND.RUBY));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('Wallet-Only Policy: All payments via Platform Wallet. Cash not accepted.', MARGIN.left + 3, y + 16.5);

  y += 26;

  ensureSpace(35);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  [
    '1. Payments strictly via Platform Wallet. No cash/direct transfer to operator/owner.',
    '2. Cancellation: 1 hr charge deducted as penalty. Balance credited to Wallet.',
    '3. Direct contact bypassing platform = Blacklisting & legal action.',
    '4. Subject to Karad, Satara, Maharashtra jurisdiction.',
  ].forEach((line, i) => doc.text(line, MARGIN.left, y + i * 4));
  y += 20;

  // Dedicated last page for digital e-sign — never overlaps prior content/footer
  doc.addPage();
  y = MARGIN.top;

  const now = new Date();
  const istParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const part = (type) => istParts.find((p) => p.type === type)?.value || '';
  const esignTimestamp = `${part('day')}/${part('month')}/${part('year')} ${part('hour')}:${part('minute')} IST`;

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Digital Signatures', MARGIN.left, y);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('IT Act 2000 Sec 3A - Digitally Verified via Platform', MARGIN.left, y + 5);
  y += 14;

  const sigW = (contentW - 8) / 2;
  const sigH = 42;
  const sigBoxes = [
    {
      role: 'Client Acknowledgement',
      name: data.clientName,
      detail: 'Digitally Verified via Platform',
    },
    {
      role: 'For Development Express',
      name: 'Om Chavan, Managing Director',
      detail: 'Digitally Verified via Platform',
    },
  ];
  sigBoxes.forEach((s, i) => {
    const x = MARGIN.left + i * (sigW + 8);
    roundRectFS(doc, x, y, sigW, sigH, 1.5, '#FFFFFF', BRAND.GOLD, 0.45);
    doc.setFillColor(...rgb(BRAND.GOLD_PALE));
    doc.rect(x, y, sigW, 7, 'F');
    doc.setDrawColor(...rgb(BRAND.GOLD));
    doc.setLineWidth(0.2);
    doc.line(x, y + 7, x + sigW, y + 7);

    doc.setTextColor(...rgb(BRAND.GOLD_DARK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(s.role.toUpperCase(), x + 3, y + 4.8);

    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const nameLines = doc.splitTextToSize(String(s.name || '—'), sigW - 6);
    doc.text(nameLines[0], x + 3, y + 16);

    doc.setTextColor(...rgb(BRAND.EMERALD));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(s.detail, x + 3, y + 26);

    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(`Timestamp: ${esignTimestamp}`, x + 3, y + 34);
  });
  y += sigH + 10;

  const certW = Math.min(140, contentW);
  const certX = MARGIN.left + (contentW - certW) / 2;
  const certH = 28;
  roundRectFS(doc, certX, y, certW, certH, 1.5, BRAND.GOLD_PALE, BRAND.GOLD, 0.35);
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('Digital Signature Certificate', certX + certW / 2, y + 6, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('Powered by eMudhra - CCA Licensed CA', certX + certW / 2, y + 12, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFontSize(6);
  doc.text('Certificate ID: [pending e-Sign integration]', certX + certW / 2, y + 18, { align: 'center' });
  doc.text('This document is digitally signed under IT Act 2000, Sec 3A', certX + certW / 2, y + 23.5, { align: 'center' });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    drawInsideHeaderFooter(doc, i, totalPages, invoiceNo, docHash, supplierGstin);
  }

  const safeId = String(bookingId).replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 40);
  const filename = `DE_Client_Invoice_PREMIUM_${safeId}.pdf`;
  if (download) doc.save(filename);
  doc.__deFilename = filename;
  return doc;
}
